import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Button, Space, Typography, Spin, Tag, Tooltip } from 'antd';
import {
  WalletOutlined,
  CalendarOutlined,
  RiseOutlined,
  FallOutlined,
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { getExpenseStatsApi } from '../../services/expenseApi';

const { Title, Text } = Typography;

const ExpensesOverview = () => {
  const [loading, setLoading] = useState(false);
  const [expenseData, setExpenseData] = useState({
    today: 0,
    thisMonth: 0,
    lastMonth: 0,
    percentageChange: 0,
  });
  const navigate = useNavigate();
  const { companyId } = useParams();

  useEffect(() => {
    fetchExpenseStats();
  }, []);

  const fetchExpenseStats = async () => {
    setLoading(true);
    try {
      const today = moment().format('YYYY-MM-DD');
      const startOfMonth = moment().startOf('month').format('YYYY-MM-DD');
      const endOfMonth = moment().endOf('month').format('YYYY-MM-DD');
      const startOfLastMonth = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
      const endOfLastMonth = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');

      // Fetch stats for today, this month, and last month
      const [todayRes, thisMonthRes, lastMonthRes] = await Promise.allSettled([
        getExpenseStatsApi({ companyId, startDate: today, endDate: today }),
        getExpenseStatsApi({ companyId, startDate: startOfMonth, endDate: endOfMonth }),
        getExpenseStatsApi({ companyId, startDate: startOfLastMonth, endDate: endOfLastMonth }),
      ]);

      let todayTotal = 0;
      let thisMonthTotal = 0;
      let lastMonthTotal = 0;

      if (todayRes.status === 'fulfilled' && todayRes.value?.success) {
        todayTotal = todayRes.value?.data?.overall?.totalAmount || 0;
      }

      if (thisMonthRes.status === 'fulfilled' && thisMonthRes.value?.success) {
        thisMonthTotal = thisMonthRes.value?.data?.overall?.totalAmount || 0;
      }

      if (lastMonthRes.status === 'fulfilled' && lastMonthRes.value?.success) {
        lastMonthTotal = lastMonthRes.value?.data?.overall?.totalAmount || 0;
      }

      // Calculate percentage change
      let percentageChange = 0;
      if (lastMonthTotal > 0) {
        percentageChange = ((thisMonthTotal - lastMonthTotal) / lastMonthTotal) * 100;
      } else if (thisMonthTotal > 0) {
        percentageChange = 100;
      }

      setExpenseData({
        today: todayTotal,
        thisMonth: thisMonthTotal,
        lastMonth: lastMonthTotal,
        percentageChange: percentageChange,
      });
    } catch (error) {
      console.error('Error fetching expense stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₹${amount?.toLocaleString('en-IN') || 0}`;
  };

  const getComparisonColor = () => {
    if (expenseData.percentageChange > 0) return '#cf1322';
    if (expenseData.percentageChange < 0) return '#3f8600';
    return '#595959';
  };

  const getComparisonIcon = () => {
    if (expenseData.percentageChange > 0) return <ArrowUpOutlined />;
    if (expenseData.percentageChange < 0) return <ArrowDownOutlined />;
    return null;
  };

  const getComparisonText = () => {
    const absChange = Math.abs(expenseData.percentageChange).toFixed(1);
    if (expenseData.percentageChange > 0) {
      return `${absChange}% higher than last month`;
    } else if (expenseData.percentageChange < 0) {
      return `${absChange}% lower than last month`;
    }
    return 'Same as last month';
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',marginTop:'20px' }}>
          <Space>
            <WalletOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            <Title level={4} style={{ margin: 0 }}>
              Expenses Overview
            </Title>
          </Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(`/company/${companyId}/expense`)}
            size="large"
          >
            Add Expense
          </Button>
        </div>
      }
      style={{ marginBottom: 24 }}
      bodyStyle={{ padding: '24px' }}
    >
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          {/* Today's Expense */}
          <Col xs={24} sm={12} lg={8}>
            <Card
              hoverable
              style={{
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                    Today's Expense
                  </Text>
                  <CalendarOutlined style={{ fontSize: '20px', color: 'rgba(255, 255, 255, 0.8)' }} />
                </div>
                <Title level={2} style={{ color: '#fff', margin: '12px 0 0 0' }}>
                  {formatCurrency(expenseData.today)}
                </Title>
                <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px' }}>
                  {moment().format('DD MMMM YYYY')}
                </Text>
              </Space>
            </Card>
          </Col>

          {/* This Month's Total */}
          <Col xs={24} sm={12} lg={8}>
            <Card
              hoverable
              style={{
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                    This Month's Total
                  </Text>
                  <WalletOutlined style={{ fontSize: '20px', color: 'rgba(255, 255, 255, 0.8)' }} />
                </div>
                <Title level={2} style={{ color: '#fff', margin: '12px 0 0 0' }}>
                  {formatCurrency(expenseData.thisMonth)}
                </Title>
                <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px' }}>
                  {moment().format('MMMM YYYY')}
                </Text>
              </Space>
            </Card>
          </Col>

          {/* Compare with Last Month */}
          <Col xs={24} sm={24} lg={8}>
            <Card
              hovering
              style={{
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                background: expenseData.percentageChange >= 0 
                  ? 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
                  : 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                    vs Last Month
                  </Text>
                  {expenseData.percentageChange >= 0 ? (
                    <RiseOutlined style={{ fontSize: '20px', color: 'rgba(255, 255, 255, 0.8)' }} />
                  ) : (
                    <FallOutlined style={{ fontSize: '20px', color: 'rgba(255, 255, 255, 0.8)' }} />
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', margin: '12px 0 0 0' }}>
                  <Title level={2} style={{ color: '#fff', margin: 0 }}>
                    {Math.abs(expenseData.percentageChange).toFixed(1)}%
                  </Title>
                  {getComparisonIcon() && (
                    <span style={{ color: '#fff', fontSize: '18px' }}>
                      {getComparisonIcon()}
                    </span>
                  )}
                </div>
                <div style={{ marginTop: '8px' }}>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px' }}>
                    {getComparisonText()}
                  </Text>
                  <br />
                  <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '11px' }}>
                    Last Month: {formatCurrency(expenseData.lastMonth)}
                  </Text>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Quick Actions */}
        <Row gutter={16} style={{ marginTop: '16px' }}>
          <Col span={24}>
            <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
              <Button
                type="default"
                onClick={() => navigate(`/company/${companyId}/expense`)}
                style={{ borderRadius: '6px' }}
              >
                View All Expenses
              </Button>
              <Button
                type="default"
                onClick={() => navigate(`/company/${companyId}/expenseReport`)}
                style={{ borderRadius: '6px' }}
              >
                View Reports
              </Button>
            </Space>
          </Col>
        </Row>
      </Spin>
    </Card>
  );
};

export default ExpensesOverview;